import { useState } from "react";
import { Button, Checkbox, Empty, Input, Modal, Select, Space } from "antd";
import { useTranslation } from "react-i18next";
import { matchesSearch } from "../utils/text-search";
import { useIsSmallScreen } from "../utils/useMediaQuery";

/**
 * Seçim modalı: alanın kendisi normal bir antd Select gibi görünür (seçilenler jeton olarak, temizle
 * düğmesiyle) ama tıklanınca açılır liste yerine tam ekranlık bir modal açılır.
 *
 * Neden: dar ekranda antd'nin açılır listesi ya klavyenin altında kalıyor ya da birkaç satır
 * yükseklikte açılıp uzun listelerde (hastane, modalite) kullanılamaz hâle geliyordu. Modalde liste
 * ekranın çoğunu kaplar, arama kutusu üstte sabit durur ve seçim parmakla rahat yapılır.
 *
 * Alan olarak GERÇEK bir Select render edilir (open={false}) — böylece jeton görünümü, temizleme ve
 * filtre çubuğundaki "dolu alanı vurgula" CSS'i (bkz. StudyFilter) hiç değişmeden çalışır.
 */
export function ModalSelect({
    value,
    onChange,
    options = [],
    fieldNames,
    mode,
    placeholder,
    modalTitle,
    optionRender,
    disabled,
    ...rest
}) {
    const { t } = useTranslation();
    const isSmallScreen = useIsSmallScreen(992);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [draft, setDraft] = useState([]);

    const labelKey = fieldNames?.label || 'label';
    const valueKey = fieldNames?.value || 'value';
    const multiple = mode === 'multiple' || mode === 'tags';

    const asList = (v) => {
        if (multiple) return Array.isArray(v) ? v : [];
        return (v === undefined || v === null) ? [] : [v];
    }

    const labelOf = (option) => option?.[labelKey];
    const valueOf = (option) => option?.[valueKey];

    // Arama, jetonda görünen metne göre yapılır; Türkçe İ/I ve aksan duyarsız (bkz. matchesSearch).
    const searchTextOf = (option) => {
        const label = labelOf(option);
        return typeof label === 'string' ? label : `${option?.searchLabel ?? ''}`;
    }

    const openModal = () => {
        if (disabled) return;
        setDraft(asList(value));
        setSearch('');
        setOpen(true);
    }

    const visibleOptions = options.filter(o => !search || matchesSearch(searchTextOf(o), search));

    const toggle = (optionValue) => {
        if (!multiple) {
            onChange && onChange(optionValue);
            setOpen(false);
            return;
        }

        setDraft(prev => prev.includes(optionValue)
            ? prev.filter(x => x !== optionValue)
            : [...prev, optionValue]);
    }

    const apply = () => {
        onChange && onChange(multiple ? draft : draft[0]);
        setOpen(false);
    }

    return (
        <>
            {/* Jeton ve temizle ikonlarına yapılan tıklama modalı AÇMAZ: kullanıcı tek tek jeton
                silerken her seferinde modal açılmasın. */}
            <div
                onClick={(e) => {
                    if (e.target?.closest?.('.ant-select-selection-item-remove, .ant-select-clear'))
                        return;
                    openModal();
                }}
            >
                <Select
                    {...rest}
                    mode={mode}
                    value={value}
                    onChange={onChange}
                    options={options}
                    fieldNames={fieldNames}
                    placeholder={placeholder}
                    disabled={disabled}
                    // Açılır liste hiç açılmaz; arama kapalı olduğu için içerideki kutu salt okunur
                    // kalır ve dokunmatik klavye açılmaz.
                    open={false}
                    showSearch={false}
                />
            </div>

            <Modal
                open={open}
                title={modalTitle || placeholder}
                onCancel={() => setOpen(false)}
                destroyOnClose
                // Filtre çubuğu mobilde bir Drawer'ın (z-index 1000) içinde yaşıyor; modal onun
                // üstünde kalsın diye z-index açıkça yükseltilir.
                zIndex={1100}
                // Konumlandırma uygulamanın genel modalıyla (core/components/Modal.js) aynı kuralda:
                // dar ekranda üstten 10px, geniş ekranda dikey ortalı. antd'nin varsayılanı üstten
                // 100px boşluk bırakıyor; dar ekranda liste + arama + altlık bu boşlukla birlikte
                // ekranı aşıyor ve modal sayfa kaydırılmadan görünmüyordu.
                centered={!isSmallScreen}
                width={isSmallScreen ? '100%' : 520}
                style={isSmallScreen ? { top: 10, margin: 10, maxWidth: 'calc(100% - 20px)' } : undefined}
                footer={multiple ? (
                    <Space>
                        <Button onClick={() => setOpen(false)}>{t('Cancel')}</Button>
                        <Button type={'primary'} onClick={apply}>
                            {draft.length > 0 ? `${t('OK')} (${draft.length})` : t('OK')}
                        </Button>
                    </Space>
                ) : (
                    <Button onClick={() => setOpen(false)}>{t('Cancel')}</Button>
                )}
                styles={{ body: { paddingTop: 8 } }}
            >
                {/* Süzme yazdıkça yapılır; ayrı bir arama düğmesine gerek yok. */}
                <Input
                    placeholder={t('Search')}
                    value={search}
                    allowClear
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ marginBottom: 8 }}
                />

                {multiple && (
                    <Space style={{ marginBottom: 8 }}>
                        <Button type={'link'} size={'small'} style={{ padding: 0 }}
                            onClick={() => setDraft(visibleOptions.map(valueOf))}>
                            {t('Select All')}
                        </Button>
                        <Button type={'link'} size={'small'} style={{ padding: 0 }}
                            onClick={() => setDraft([])}>
                            {t('Clear')}
                        </Button>
                    </Space>
                )}

                {/* Kaydırma YALNIZ listede: arama kutusu ve "Hepsini Seç" hep görünür kalsın.
                    Yükseklik başlık + arama + altlık payı düşülerek hesaplanır, böylece modalın
                    tamamı ekrana sığar ve sayfanın kendisi kaymaz. */}
                <div style={{ maxHeight: 'calc(100vh - 260px)', minHeight: 120, overflowY: 'auto' }}>
                    {visibleOptions.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}

                    {visibleOptions.map((option) => {
                        const optionValue = valueOf(option);
                        const checked = draft.includes(optionValue);

                        return (
                            <div
                                key={`${optionValue}`}
                                onClick={() => toggle(optionValue)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '10px 4px',
                                    borderBottom: '1px solid var(--border)',
                                    background: checked ? 'var(--accent-soft)' : undefined,
                                    cursor: 'pointer',
                                }}
                            >
                                {multiple && <Checkbox checked={checked} />}
                                <span style={{ flex: 1 }}>
                                    {optionRender ? optionRender(option) : labelOf(option)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </Modal>
        </>
    );
}

/**
 * Geniş ekranda normal antd Select, dar ekranda modal seçici.
 *
 * `modalOnMobile` açıkça verilmedikçe davranış hiç değişmez — bileşenin kullanıldığı diğer ekranlar
 * (cihaz formları, mutabakat vb.) eskisi gibi açılır listeyle çalışmaya devam eder.
 */
export default function ResponsiveSelect({ modalOnMobile, mobileBreakpoint = 992, modalTitle, optionRender, ...props }) {
    const isMobile = useIsSmallScreen(mobileBreakpoint);

    if (modalOnMobile && isMobile) {
        return <ModalSelect {...props} modalTitle={modalTitle} optionRender={optionRender} />;
    }

    return <Select {...props} optionRender={optionRender} />;
}
